import { useCallback, useEffect, useState } from 'react';
import { Bot, Eye, MailOpen, Maximize2, Minimize2, Reply, RotateCcw, Send, Shield, Star, Trash2, X } from '../../icons';
import { getMail, patchMail, type MailDetail as MailDetailData, type MailRecipientDetail } from '../../api/mail';
import { sanitizeMailHtml, sanitizeExternalMailHtml } from '../../lib/sanitize';
import type { ComposePrefill } from './ComposeModal';
import { externalChip, type PickedUser } from './RecipientPicker';
import { useAuthStore } from '../../store/authStore';
import { tMail } from '../../lib/mail-i18n';
import { useMailPrefs } from './mailPrefs';
import { MailAddressRows } from './MailAddressRows';
// 주소록 (2026-09-08) — 보낸사람 이름을 누르면 사람 카드(F3-2), 머리에서 여럿을 한꺼번에(E1-4).
import { PersonCard } from '../../components/PersonCard/PersonCard';
import { ContactPickModal, type PickCandidate } from './ContactPickModal';
import { tContacts } from '../../lib/contacts-i18n';
import { UserPlus } from '../../icons';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailAttachments } from './MailAttachments';
import { formatDateTime } from '../../lib/base-i18n';
// AxiEditor 로 쓴 메일 (2026-10-14) — 서식째 그리기 · 원문 HTML 인용 · YouTube 미리보기.
import { buildQuotedReplyHtml, sanitizeMailHtmlRich } from '../../components/editor/axi/mail-html';
import { activateYoutubeLite } from '../../components/editor/axi/youtube-lite';
import '../../components/editor/axi/axi-content.css';
import { proxyExternalImages } from './mail-image-proxy';

interface MailDetailProps {
  mailId: string | null;
  /** 상위 list 의 row 갱신용 — 별표/휴지통/읽음 토글 시 호출. */
  onLocalUpdate: (mailId: string, partial: { isStarred?: boolean; isTrash?: boolean; isRead?: boolean; isSpam?: boolean }) => void;
  /**
   * 답장 / 전체답장 / 전달 — 부모가 작성 화면으로 데려간다.
   * ★ 두 번째 값(mode)은 **선택**이다. 안 읽는 부모는 지금까지와 똑같이 동작한다.
   */
  onCompose?: (prefill: ComposePrefill, mode?: 'reply' | 'replyAll' | 'forward') => void;
  /**
   * 열림 방식 도구 (2026-08-21) — **선택**이다. 안 주면 버튼이 안 나오고 지금까지와 같다.
   * 상세는 자기가 어떻게 열려 있는지 모른다 — 그 결정은 MailPage 가 쥔다.
   */
  isFull?: boolean;
  onToggleFull?: () => void;
  onClose?: () => void;
  /**
   * 이 메일이 **이 자리에서 빠져나갔다** — 휴지통·스팸으로 옮긴 뒤 부르는 손잡이 (2026-08-22).
   *
   * ★ `onClose` 와 따로 둔다. `onClose` 는 **닫기 버튼을 그릴지**를 정하는 값이라, 팝업보기
   *   처럼 자기 X 를 이미 가진 자리에 넘기면 X 가 둘이 된다. "치웠으니 닫아라" 는 버튼과
   *   무관한 일이므로 손잡이를 나눈다.
   */
  onMovedAway?: () => void;
  /**
   * 「연락처 열기」 — 주소록으로 **간다**(가는 것이 목적인 유일한 행동이다).
   * ★ 선택이다. 안 주면 그 단추가 아무 일도 안 하는 대신, 부모가 이동을 쥔다 —
   *   상세가 제멋대로 `window.location` 을 만지면 SPA 가 통째로 새로고침된다.
   */
  onOpenContacts?: () => void;
}

/**
 * MailDetail (Phase 3 P3-MAIL-02) — 우측 상세 패널.
 *
 * 책임:
 *   - mailId 변경 시 GET /mail/messages/:id fetch.
 *   - sender / recipients / sentAt / subject / body 표시.
 *   - body 는 plain text. HTML 본문은 안전을 위해 textContent (DOMPurify 도입은 후속).
 *   - 별표 / 휴지통 / 읽음 PATCH endpoint 결선 — optimistic update + 응답으로 sync.
 *   - 메일 진입 시 자동 isRead=true PATCH (myStatus.isRead === false 인 경우).
 *
 * mailId === null 이면 비활성 상태 (placeholder).
 */
export function MailDetail({ mailId, onLocalUpdate, onCompose, isFull, onToggleFull, onClose, onMovedAway, onOpenContacts }: MailDetailProps) {
  /*
   * 주소록에 담기 (2026-09-08) — 둘을 가른다.
   *   · `pickOpen` : 이 메일의 주소 **전부**를 표로 올린다(정본 E1-4).
   *   · `pickOne`  : 사람 카드에서 «이 사람 하나»를 담는다 — 같은 표를 한 줄짜리로 연다.
   * 한 줄이라고 다른 화면을 만들지 않는다 — 담는 자리가 둘이면 규칙도 둘이 된다.
   */
  /**
   * 담기 팝업에 올릴 사람들 (2026-09-08 사용자 지적).
   *
   * ★★★ 담기는 **줄마다** 한다 — 보낸사람 줄의 단추는 보낸사람을, 받는사람 줄의 단추는
   *    받는사람을 올린다. 「이 메일 전부」로 뭉뚱그리면 **어느 줄을 담는지**가 사라진다.
   * ★ 사람 카드의 「주소록에 추가」도 같은 팝업을 한 줄짜리로 연다 — 담는 자리가 둘이면
   *   규칙(잠금 · 이유 적기 · 비운 이름)도 둘이 된다.
   */
  const [pickList, setPickList] = useState<PickCandidate[] | null>(null);
  /** 공유 주소록에 담을 수 있나 — 2026-09-15 부터 모두(서버 #1374 · 담기만, 고치기 · 지우기는 관리자). */
  const canShareContacts = true;
  const myEmail = useAuthStore((s) => s.user?.email ?? null);
  const myUserId = useAuthStore((s) => (s.user ? String(s.user.id) : null));
  const [detail, setDetail] = useState<MailDetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'star' | 'trash' | 'spam' | null>(null);
  /**
   * 치우기 전에 묻는다 (2026-08-22).
   *
   * 여태 휴지통·스팸은 **말없이** 처리되고 메일은 그대로 열려 있었다 — 방금 버린 메일을
   * 계속 읽고 있는 셈이라, 됐는지 안 됐는지 알 수가 없었다.
   *
   * ★ **되돌릴 때는 묻지 않는다.** 휴지통·스팸함에서 되돌리는 것은 되돌릴 수 있는 일이라,
   *   확인창이 도움이 아니라 방해다.
   */
  const [confirmMove, setConfirmMove] = useState<'trash' | 'spam' | null>(null);
  // 원본 구분자(migration 110) — 기본값 true 라 값이 오기 전에도 지금까지처럼 인용을 붙인다.
  const { prefs: { quoteDivider, addrDisplay, blockImages } } = useMailPrefs();

  // mailId 변경 시 fetch + 자동 read 처리.
  useEffect(() => {
    if (!mailId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMail(mailId)
      .then(async (data) => {
        if (cancelled) return;
        setDetail(data);
        // 자동 read 처리 — myStatus.isRead 가 false 면 PATCH 호출 + 부모 list 갱신.
        if (data.myStatus && !data.myStatus.isRead) {
          try {
            const res = await patchMail(mailId, { isRead: true });
            if (!cancelled) {
              setDetail((prev) => (prev ? { ...prev, myStatus: res.myStatus } : prev));
              if (res.myStatus) onLocalUpdate(mailId, { isRead: res.myStatus.isRead });
            }
          } catch {
            // 자동 read 실패는 silent — UI 영향 없음.
          }
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'mail_detail_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailId]);

  const handleStar = useCallback(async () => {
    if (!detail || !detail.myStatus || !mailId || busy) return;
    const next = !detail.myStatus.isStarred;
    setBusy('star');
    // optimistic
    setDetail((prev) => (prev && prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isStarred: next } } : prev));
    onLocalUpdate(mailId, { isStarred: next });
    try {
      const res = await patchMail(mailId, { isStarred: next });
      setDetail((prev) => (prev ? { ...prev, myStatus: res.myStatus } : prev));
      if (res.myStatus) onLocalUpdate(mailId, { isStarred: res.myStatus.isStarred });
    } catch (err) {
      // rollback
      setDetail((prev) => (prev && prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isStarred: !next } } : prev));
      onLocalUpdate(mailId, { isStarred: !next });
      setError(err instanceof Error ? err.message : 'star_failed');
    } finally {
      setBusy(null);
    }
  }, [busy, detail, mailId, onLocalUpdate]);

  const buildReplyPrefill = useCallback(
    (mode: 'reply' | 'replyAll' | 'forward'): ComposePrefill => {
      if (!detail) return {};
      // 밖에서 온 메일이면 보낸 사람이 우리 사용자가 아니다 (migration 112·113) — 사용자 id 가
      // 아니라 **주소 칩**으로 만들어야 서버가 SMTP 로 내보낸다.
      const senderUser: PickedUser = detail.sender.isExternal
        ? externalChip(detail.sender.email)
        : { id: detail.sender.id, name: detail.sender.name, email: detail.sender.email };
      const allRecipients: PickedUser[] = detail.recipients.map((r) => (
        r.isExternal || r.userId === ''
          ? externalChip(r.email)
          : { id: r.userId, name: r.name, email: r.email }
      ));
      const subjectPrefix = mode === 'forward' ? 'Fwd: ' : 'Re: ';
      const cleanSubject = detail.subject.replace(/^(Re:|Fwd:)\s*/i, '');
      const subject = `${subjectPrefix}${cleanSubject}`;

      const quotedHeader = `\n\n${tMail('mail.detail.quote_prefix')}\n${tMail('mail.detail.from_label')} ${detail.sender.name} <${detail.sender.email}>\n${tMail('mail.detail.sent_at_label')} ${detail.sentAt ?? detail.createdAt}\n${tMail('mail.detail.subject_label')} ${detail.subject}\n\n`;
      const bodyPlain = stripHtml(detail.body, detail.bodyType);
      // 원본 구분자(migration 110) — 끄면 인용을 아예 붙이지 않고 빈 본문으로 시작한다.
      const quotedBody = quoteDivider
        ? quotedHeader + bodyPlain.split('\n').map((l) => `> ${l}`).join('\n')
        : '';
      /*
       * HTML 로 쓸 때(AxiEditor)의 인용 — 원문을 «HTML 그대로» blockquote 로 (2026-10-14 사용자 결정).
       * ★ 원문은 화면에 그리던 거르개로 거른 것을 넣는다(바깥 메일은 바깥 정책 · 우리 메일은 리치).
       * ★ 평문으로 쓸 때는 위 `quotedBody` 그대로다 — ComposeModal 이 쓰기 방식으로 고른다.
       */
      const sentLabel = detail.sentAt ?? detail.createdAt;
      const quotedHtml = quoteDivider
        ? buildQuotedReplyHtml({
            body: detail.bodyType === 'HTML'
              ? (detail.sender.isExternal === true
                ? sanitizeExternalMailHtml(detail.body, { blockImages })
                : sanitizeMailHtmlRich(detail.body, { blockImages }))
              : detail.body,
            isHtml: detail.bodyType === 'HTML',
            headerTitle: tMail('mail.detail.quote_prefix'),
            headerLines: [
              `${tMail('mail.detail.from_label')} ${detail.sender.name} <${detail.sender.email}>`,
              `${tMail('mail.detail.sent_at_label')} ${sentLabel}`,
              `${tMail('mail.detail.subject_label')} ${detail.subject}`,
            ],
          })
        : '';

      /*
       * ★★ **받은 그 계정으로 답장한다** (2026-09-07). 안 이어 붙이면 서버가 기본 발신 계정을
       *   고르므로, 네이버로 온 메일에 답장해도 하이웍스에서 나간다 — 받는 쪽은 자기가 쓴 적
       *   없는 주소에서 답을 받는다. 전달도 같다(원본을 받은 자리에서 이어 보내는 것이 자연스럽다).
       * ★ 사내 메일이면 null 이고, 그때는 지금까지처럼 기본 계정이 고른다.
       */
      const sendingAccountId = detail.externalAccountId ?? null;

      if (mode === 'reply') {
        return { to: [senderUser], subject, body: quotedBody, bodyHtml: quotedHtml, sendingAccountId };
      }
      if (mode === 'replyAll') {
        // 본인 제외 + sender 우선 포함
        const others = allRecipients.filter((u) => u.id !== senderUser.id && u.id !== myUserId);
        return { to: [senderUser, ...others], subject, body: quotedBody, bodyHtml: quotedHtml, sendingAccountId };
      }
      // forward
      return { to: [], subject, body: quotedBody, bodyHtml: quotedHtml, sendingAccountId };
    },
    [blockImages, detail, myUserId, quoteDivider],
  );

  /**
   * 스팸 신고 / 되돌리기 (migration 111, 2026-08-20).
   *
   * ★ 신고는 **차단까지 따라오는** 동작이라 삭제 옆이 아니라 별도 버튼이고, 되돌릴 때는
   *   차단도 함께 풀린다(서버가 처리) — 안 그러면 다음 메일이 또 스팸함으로 간다.
   */
  const runSpam = useCallback(async (next: boolean) => {
    if (!detail || !detail.myStatus || !mailId || busy) return;
    setConfirmMove(null);
    setBusy('spam');
    setDetail((prev) => (prev && prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isSpam: next } } : prev));
    onLocalUpdate(mailId, { isSpam: next });
    try {
      // 상세에서 누를 때는 차단까지 묻지 않는다 — 목록의 확인창이 그 몫이다.
      const res = await patchMail(mailId, { isSpam: next });
      setDetail((prev) => (prev ? { ...prev, myStatus: res.myStatus } : prev));
      onLocalUpdate(mailId, { isSpam: res.myStatus?.isSpam ?? next });
      // 치웠으면 이 자리에 남아 있을 이유가 없다 — 목록으로 돌아간다.
      if (next) onMovedAway?.();
    } catch (err) {
      setDetail((prev) => (prev && prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isSpam: !next } } : prev));
      onLocalUpdate(mailId, { isSpam: !next });
      setError(err instanceof Error ? err.message : 'spam_failed');
    } finally {
      setBusy(null);
    }
  }, [busy, detail, mailId, onLocalUpdate, onMovedAway]);

  /** 치울 때만 묻고, 되돌릴 때는 곧장 한다. */
  const handleSpam = useCallback(() => {
    if (!detail?.myStatus || busy) return;
    if (detail.myStatus.isSpam) { void runSpam(false); return; }
    setConfirmMove('spam');
  }, [busy, detail, runSpam]);

  /**
   * 치우기 — **받는 쪽과 보낸 쪽이 하는 일이 다르다** (migration 115, 2026-08-23).
   *
   *   · 받은 메일 : 내 수신자 행의 isTrash → 휴지통으로. 언제든 되돌린다.
   *   · 보낸 메일 : 내 보낸편지함에서만 가린다. **받는 사람 편지함에는 그대로 남는다.**
   *   · 초안      : 아무에게도 안 갔으므로 통째로 지운다. **되돌릴 수 없다.**
   *
   * 서버는 셋 다 같은 자리(PATCH isTrash)로 받고 안에서 갈라 처리한다 — 화면은 어느 쪽인지만
   * 알면 되고, 그 판단은 `myStatus` 가 있느냐 `senderStatus` 가 있느냐로 갈린다.
   */
  const runTrash = useCallback(async (next: boolean) => {
    if (!detail || !mailId || busy) return;
    const asSender = !detail.myStatus && detail.senderStatus != null;
    if (!detail.myStatus && !asSender) return;
    setConfirmMove(null);
    setBusy('trash');
    // 상세가 든 상태 — 받는 쪽이면 myStatus, 보낸 쪽이면 senderStatus 를 먼저 뒤집는다.
    setDetail((prev) => {
      if (!prev) return prev;
      if (asSender) {
        return prev.senderStatus ? { ...prev, senderStatus: { ...prev.senderStatus, isTrash: next } } : prev;
      }
      return prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isTrash: next } } : prev;
    });
    /*
     * ★ 2026-08-23 — 목록 갱신은 **어느 쪽이든** 부른다.
     *   보낸 쪽에서 이것을 빼 뒀더니, 상세에서 치워도 목록에 그 줄이 그대로 남아
     *   **다른 메뉴에 갔다 와야 사라졌다**(사용자 지적). 목록이 줄을 감출지 말지는
     *   부모(handleLocalUpdate)가 지금 보고 있는 메일함을 기준으로 판단한다.
     */
    onLocalUpdate(mailId, { isTrash: next });
    try {
      const res = await patchMail(mailId, { isTrash: next });
      setDetail((prev) => (prev ? { ...prev, myStatus: res.myStatus, senderStatus: res.senderStatus ?? prev.senderStatus } : prev));
      // 서버가 확정한 값으로 한 번 더 맞춘다 — 둘 다 없으면(구 서버) 방금 보낸 값을 믿는다.
      onLocalUpdate(mailId, { isTrash: res.myStatus?.isTrash ?? res.senderStatus?.isTrash ?? next });
      if (next) onMovedAway?.();
    } catch (err) {
      setDetail((prev) => {
        if (!prev) return prev;
        if (asSender) {
          return prev.senderStatus ? { ...prev, senderStatus: { ...prev.senderStatus, isTrash: !next } } : prev;
        }
        return prev.myStatus ? { ...prev, myStatus: { ...prev.myStatus, isTrash: !next } } : prev;
      });
      onLocalUpdate(mailId, { isTrash: !next });
      setError(err instanceof Error ? err.message : 'trash_failed');
    } finally {
      setBusy(null);
    }
  }, [busy, detail, mailId, onLocalUpdate, onMovedAway]);

  const handleTrash = useCallback(() => {
    if (busy || !detail) return;
    const status = detail.myStatus ?? detail.senderStatus;
    if (!status) return;
    // 되돌릴 때는 묻지 않는다 — 되돌릴 수 있는 일에 확인창은 방해다.
    // ★ 초안은 되돌리기가 없다(이미 지워졌으면 화면에 없다).
    if (status.isTrash && !detail.senderStatus?.isDraft) { void runTrash(false); return; }
    setConfirmMove('trash');
  }, [busy, detail, runTrash]);

  if (!mailId) {
    return (
      <section className="mail-detail mail-detail--empty" aria-label="메일 상세">
        <div className="mail-detail-placeholder">
          <MailOpen size={48} aria-hidden />
          <div className="mail-detail-placeholder-title">{tMail('mail.detail.empty_title')}</div>
          <div className="mail-detail-placeholder-sub">{tMail('mail.detail.empty_desc')}</div>
        </div>
      </section>
    );
  }

  if (loading && !detail) {
    return <section className="mail-detail" aria-busy>불러오는 중…</section>;
  }
  if (error && !detail) {
    return <section className="mail-detail mail-detail-error">{error}</section>;
  }
  if (!detail) return null;

  const isAi = detail.sender.name === 'AI 비서';
  /**
   * 밖에서 온 메일 (migration 112).
   *
   * ★ 2026-08-21 — **답장 잠금을 풀었다.** 그동안 막아 둔 이유는 외부 주소로 보내는 길이
   *   아예 없었기 때문인데, migration 113 으로 그 길이 생겼다. 이제 답장은 보낸 주소를
   *   **주소 칩**으로 실어 계정의 SMTP 로 나간다.
   */
  const isExternal = detail.sender.isExternal === true;
  /**
   * 보낸 쪽 치우기 버튼의 이름 — 하는 일이 셋으로 갈리므로 이름도 갈린다.
   * 초안은 "지우기"(되돌릴 수 없다) · 보낸 메일은 "치우기"(회수가 아니다) · 치운 것은 "되돌리기".
   */
  /*
   * 치우기 확인창은 **하는 일마다 문구가 다르다.** 한 문구로 뭉치면 거짓말이 된다 —
   * 특히 보낸 메일은 "받는 사람 편지함에는 그대로 남는다" 를 말하지 않으면 회수로 읽힌다.
   */
  const trashConfirmKeys = detail.senderStatus?.isDraft
    ? { title: 'mail.detail.confirm_draft_title', desc: 'mail.detail.confirm_draft_desc', ok: 'mail.detail.confirm_draft_ok' } as const
    : detail.senderStatus
      ? { title: 'mail.detail.confirm_sent_title', desc: 'mail.detail.confirm_sent_desc', ok: 'mail.detail.confirm_sent_ok' } as const
      : { title: 'mail.detail.confirm_trash_title', desc: 'mail.detail.confirm_trash_desc', ok: 'mail.detail.confirm_trash_ok' } as const;

  const senderTrashLabel = detail.senderStatus?.isDraft
    ? tMail('mail.detail.btn_delete_draft_aria')
    : detail.senderStatus?.isTrash
      ? tMail('mail.detail.btn_restore_sent_aria')
      : tMail('mail.detail.btn_trash_sent_aria');
  const time = formatDetailTime(detail.sentAt ?? detail.createdAt);

  return (
    <section className="mail-detail" aria-label={detail.subject}>
      <header className="mail-detail-header">
        {/*
          ★ 2026-08-22 — **도구에게 자기 줄을 줬다.** 여태 제목과 도구 여덟 개가 같은 줄을
            다퉜고, 자리가 모자라면 도구가 다음 줄로 내려갔다(MailPageView.css 의 flex-wrap).
            좌우로 열면 거의 항상 내려가서, 열 때마다 버튼 자리가 달라졌다.
          ★ 전체보기·닫기를 `myStatus` 밖으로 꺼냈다 — 여태 이 둘이 `myStatus &&` 안에 있어서
            **내가 보낸 메일(myStatus 가 null)에는 닫기 버튼이 아예 없었다.**
        */}
        <div className="mail-detail-toolbar">
          <div className="mail-detail-reply-actions">
            <button
              type="button"
              className="mail-detail-reply-button"
              onClick={() => onCompose?.(buildReplyPrefill('reply'), 'reply')}
              aria-label={tMail('mail.detail.btn_reply')}
              title={tMail('mail.detail.btn_reply')}
              disabled={!onCompose}
            >
              <Reply size={14} aria-hidden />
              <span className="mail-detail-reply-label">{tMail('mail.detail.btn_reply')}</span>
            </button>
            {/*
              ★ 2026-08-21 — 전체답장의 잠금을 풀었다. 여태 `recipients.length <= 1` 이면 껐는데,
                받는 사람이 나 하나인 메일(밖에서 온 메일이 대부분 그렇다)에서는 **늘 꺼져 있었다**.
                참여자가 하나뿐이면 답장과 같은 결과가 나올 뿐이므로, 끄는 대신 그대로 동작시킨다.
            */}
            <button
              type="button"
              className="mail-detail-reply-button"
              onClick={() => onCompose?.(buildReplyPrefill('replyAll'), 'replyAll')}
              aria-label={tMail('mail.detail.btn_reply_all')}
              title={tMail('mail.detail.btn_reply_all_title')}
              disabled={!onCompose}
            >
              <Reply size={14} aria-hidden />
              <span className="mail-detail-reply-label">{tMail('mail.detail.btn_reply_all')}</span>
            </button>
            <button
              type="button"
              className="mail-detail-reply-button"
              onClick={() => onCompose?.(buildReplyPrefill('forward'), 'forward')}
              aria-label={tMail('mail.detail.btn_forward')}
              title={tMail('mail.detail.btn_forward')}
              disabled={!onCompose}
            >
              <Send size={14} aria-hidden />
              <span className="mail-detail-reply-label">{tMail('mail.detail.btn_forward')}</span>
            </button>
          </div>
          <span className="mail-detail-toolbar-gap" aria-hidden />
          {detail.myStatus && (
            <div className="mail-detail-actions">
              <button
                type="button"
                className={`mail-detail-action${detail.myStatus.isStarred ? ' mail-detail-action--star-active' : ''}`}
                onClick={() => void handleStar()}
                disabled={busy !== null}
                aria-label={detail.myStatus.isStarred ? tMail('mail.detail.btn_unstar_aria') : tMail('mail.detail.btn_star_aria')}
                title={detail.myStatus.isStarred ? tMail('mail.detail.btn_unstar_aria') : tMail('mail.detail.btn_star_aria')}
                aria-pressed={detail.myStatus.isStarred}
              >
                <Star size={17} fill={detail.myStatus.isStarred ? 'currentColor' : 'none'} />
              </button>
              <button
                type="button"
                className={`mail-detail-action${detail.myStatus.isSpam ? ' mail-detail-action--spam-active' : ''}`}
                onClick={handleSpam}
                disabled={busy !== null}
                aria-label={detail.myStatus.isSpam ? tMail('mail.detail.btn_not_spam_aria') : tMail('mail.detail.btn_spam_aria')}
                title={detail.myStatus.isSpam ? tMail('mail.detail.btn_not_spam_aria') : tMail('mail.detail.btn_spam_aria')}
                aria-pressed={detail.myStatus.isSpam ?? false}
              >
                <Shield size={17} />
              </button>
              {/*
                ★ 2026-08-23 — **이미 치워진 메일에서는 되돌리기 아이콘으로 바뀐다.**
                  여태 어느 쪽이든 붉은 휴지통 하나였다. 이 도구 줄에서 붉은색은 "그 상태가
                  켜져 있다" 는 뜻인데, 사람은 붉은 휴지통을 **지우는 단추**로 읽는다.
                  그래서 휴지통함에서 한 번 눌러 되돌려 놓고(아무것도 안 지워진 것처럼 보인다)
                  다시 눌러서야 지워졌다 — "두 번 눌러야 지워진다"(사용자 지적).
                  같은 그림이 반대 일을 하지 않게, 되돌리기는 되돌리기처럼 생긴다.
              */}
              <button
                type="button"
                className="mail-detail-action"
                onClick={handleTrash}
                disabled={busy !== null}
                aria-label={detail.myStatus.isTrash ? tMail('mail.detail.btn_restore_aria') : tMail('mail.detail.btn_trash_aria')}
                title={detail.myStatus.isTrash ? tMail('mail.detail.btn_restore_aria') : tMail('mail.detail.btn_trash_aria')}
              >
                {detail.myStatus.isTrash ? <RotateCcw size={17} /> : <Trash2 size={17} />}
              </button>
            </div>
          )}
          {/*
            ★ 2026-08-23 — **내가 보낸 메일·초안의 치우기.**
              여태 도구가 통째로 `myStatus &&` 안에 있어서, 보낸편지함·임시보관함에서는 별·스팸·
              휴지통이 아예 안 그려졌다(서버도 404 를 줬다). 보낸 사람에게 뜻이 있는 것은 치우기
              하나뿐이라 그것만 그린다 — 내가 나를 스팸으로 신고할 일은 없다.
          */}
          {!detail.myStatus && detail.senderStatus && (
            <div className="mail-detail-actions">
              {/*
                ★ 붉은색은 여기서 "그 상태가 켜져 있다" 는 뜻이다 — 초안을 늘 붉게 칠하면
                  **이미 치워진 것처럼** 읽힌다. 되돌릴 수 없다는 말은 확인창이 한다.
              */}
              <button
                type="button"
                className="mail-detail-action"
                onClick={handleTrash}
                disabled={busy !== null}
                aria-label={senderTrashLabel}
                title={senderTrashLabel}
              >
                {detail.senderStatus.isTrash ? <RotateCcw size={17} /> : <Trash2 size={17} />}
              </button>
            </div>
          )}
          {(onToggleFull || onClose) && (
            <div className="mail-detail-actions mail-detail-actions--window">
              {onToggleFull && (
                <button
                  type="button"
                  className="mail-detail-action"
                  onClick={onToggleFull}
                  aria-label={isFull ? tMail('mail.detail.btn_exit_full') : tMail('mail.detail.btn_full')}
                  title={isFull ? tMail('mail.detail.btn_exit_full') : tMail('mail.detail.btn_full')}
                  aria-pressed={isFull === true}
                >
                  {isFull ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  className="mail-detail-action"
                  onClick={onClose}
                  aria-label={tMail('mail.detail.btn_close')}
                  title={tMail('mail.detail.btn_close')}
                >
                  <X size={17} />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mail-detail-header-row mail-detail-header-row--title">
          <h2 className="mail-detail-subject">{detail.subject || tMail('mail.item.subject_empty')}</h2>
        </div>

        <div className="mail-detail-sender-row">
          <div className={`mail-detail-avatar${isAi ? ' mail-detail-avatar--ai' : ''}`} aria-hidden>
            {isAi ? <Bot size={18} /> : (detail.sender.name || '?').slice(0, 1)}
          </div>
          <div className="mail-detail-sender-meta">
            <div className="mail-detail-sender-name">
              {/*
                ★ 이름을 누르면 사람 카드가 뜬다(정본 F3-2). 이름 «글자»는 그대로 두고
                  버튼으로 감싸기만 한다 — 클래스도 문구도 안 바뀌므로 회귀가 없다.
              */}
              <PersonCard
                email={detail.sender.email}
                name={detail.sender.name}
                isMe={myUserId != null && detail.sender.id === myUserId}
                onSendMail={(to) => onCompose?.({ to: [externalChip(to)], subject: '', body: '' })}
                onAdd={(email, name) => setPickList([{ email, name }])}
                onOpenContact={() => onOpenContacts?.()}
              >
                {({ ref, toggle, open }) => (
                  <button
                    type="button"
                    ref={ref as React.RefObject<HTMLButtonElement>}
                    className="mail-detail-sender-trigger"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    onClick={toggle}
                  >
                    {detail.sender.name}
                  </button>
                )}
              </PersonCard>
              {/*
                메일주소 표시방식 (2026-09-05) — 「환경설정 > 기본환경」.
                ★ 기본은 **지금 하던 대로**(이름 + 메일주소)다. 「이름」을 고른 사람에게만 주소가
                  빠진다 — 사용자가 이 항목의 기본을 지정하지 않아, 아무것도 안 고친 사람의
                  화면이 달라지지 않는 쪽을 기본으로 뒀다.
                ★ 주소를 감춰도 «밖에서 온 메일» 표식은 그대로 둔다 — 그것은 주소가 아니라
                  「어디서 왔나」다.
              */}
              {addrDisplay === 'NAME_EMAIL' && (
                <span className="mail-detail-sender-email"> &lt;{detail.sender.email}&gt;</span>
              )}
              {isExternal && (
                <span className="mail-item-external" title={tMail('mail.sender.external_title')}>
                  {tMail('mail.sender.external_badge')}
                </span>
              )}
              {/*
                ★ 담기 단추는 **보낸사람 뒤**에 선다(2026-09-08 사용자 지적).
                  누르면 «이 줄의 사람» 이 팝업에 오른다 — 나 자신이면 아예 안 그린다.
              */}
              {toCandidates([{ id: detail.sender.id, name: detail.sender.name, email: detail.sender.email }], myUserId, myEmail).length > 0 && (
                <button
                  type="button"
                  className="mail-detail-pick-btn"
                  aria-label={tContacts('contacts.pick.open')}
                  title={tContacts('contacts.pick.open')}
                  onClick={() => setPickList(toCandidates(
                    [{ id: detail.sender.id, name: detail.sender.name, email: detail.sender.email }], myUserId, myEmail,
                  ))}
                >
                  <UserPlus size={14} aria-hidden />
                </button>
              )}
            </div>
            {isExternal && (
              <div className="mail-detail-recipients">{tMail('mail.detail.external_reply_hint')}</div>
            )}
            {/*
              ★ 2026-08-22 — **받는사람과 참조를 나눠 적는다.** 여태 세 종류를 한 줄에 이어 붙여
                "받는 사람:" 이라고 찍었다 — 참조로 받은 사람이 받는사람으로 보였고, 사람이 많으면
                줄이 잘려 누가 더 있는지 알 길이 없었다. 종류는 서버가 이미 준다(type=TO|CC|BCC).
            */}
            {/*
              담기는 **줄 끝에 아이콘 하나**다 (2026-09-08 사용자 지적).
              ★ 처음엔 주소마다 담기 아이콘을 붙였는데, 주소가 넷이면 아이콘도 넷이라
                **이름이 안 보였다**. 줄 끝의 하나가 «그 줄 전체»를 담는다 —
                받는사람 줄은 받는사람 전부, 참조 줄은 참조 전부.
              ★ 한 사람만 담고 싶으면 **이름을 눌러** 사람 카드(F3-2)에서 담는다.
            */}
            <MailAddressRows
              recipients={detail.recipients}
              myUserId={myUserId}
              renderGroupAction={(_key, list) => {
                /* 나를 뺀 «그 줄의 사람들». 나 혼자면 단추 자체를 안 그린다. */
                const cands = toCandidates(list.map((r) => ({ id: r.userId, name: r.name, email: r.email })), myUserId, myEmail);
                if (cands.length === 0) return null;
                return (
                  <button
                    type="button"
                    className="mail-detail-pick-btn"
                    aria-label={tContacts('contacts.pick.open')}
                    title={tContacts('contacts.pick.open')}
                    onClick={() => setPickList(cands)}
                  >
                    <UserPlus size={14} aria-hidden />
                  </button>
                );
              }}
            />
            {time && <div className="mail-detail-time">{time}</div>}
          </div>
        </div>

        {/*
          수신확인 (2026-08-21) — **내가 보낸 메일을 볼 때만** 서버가 읽음 정보를 준다.
          ★ 밖으로 보낸 주소에는 그 칸이 아예 안 온다 → "확인 불가" 라고 적는다.
            빈 칸으로 두면 "아직 안 읽음" 으로 읽히는데, 그건 모르는 것을 아는 척하는 것이다.
        */}
        {detail.recipients.some((r) => r.isRead !== undefined) && (
          <section className="mail-receipt" aria-label={tMail('mail.receipt.heading')}>
            <p className="mail-receipt-h">
              <Eye size={13} aria-hidden />
              {tMail('mail.receipt.heading')}
              <span>
                {tMail('mail.receipt.read_of')
                  .replace('{a}', String(detail.recipients.filter((r) => r.isRead === true).length))
                  .replace('{b}', String(detail.recipients.length))}
              </span>
            </p>
            <ul className="mail-receipt-list">
              {detail.recipients.map((r) => {
                const na = r.isRead === undefined;
                return (
                  <li
                    key={r.id}
                    className={`mail-receipt-row${r.isRead ? ' mail-receipt-row--read' : ''}${na ? ' mail-receipt-row--na' : ''}`}
                  >
                    <span className="mail-receipt-name">{r.name}</span>
                    <span>{r.type}</span>
                    <span className="mail-receipt-when">
                      {na
                        ? tMail('mail.receipt.external_na')
                        : (r.isRead
                          ? (r.readAt
                            ? formatDateTime(r.readAt)
                            : tMail('mail.receipt.heading'))
                          : tMail('mail.receipt.unread'))}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </header>

      <div className="mail-detail-body" role="article">
        {/* HIGH-7 봉합 — bodyType=HTML 시 DOMPurify 로 sanitize 후 안전 렌더.
            backend 의 sanitize-html 과 양쪽 적용 (defense in depth). */}
        {detail.bodyType === 'HTML' ? (
          <div
            // 2026-10-14 — AxiEditor 본문의 배치(도형·슬라이드)를 이 상자 안에 가둔다(axi-content.css).
            className="mail-detail-body-html axi-board-content--axe"
            // YouTube 미리보기 — 누르면 그 자리에서 재생(에디터 번들 없이).
            onClick={(e) => {
              const t = e.target as Element;
              if (t.closest('a')) return;
              const wrap = t.closest<HTMLElement>('.open-axe-yt-lite');
              if (wrap && activateYoutubeLite(wrap)) e.preventDefault();
            }}
            // eslint-disable-next-line react/no-danger -- sanitize 통과 후 안전.
            // ★ 바깥에서 온 메일은 style 을 살리는 정책으로 거른다 — 우리 정책으로 거르면
            //   서버가 남겨 둔 표·서식을 화면이 다시 지워 맨 글자로 무너진다.
            /* 본문 이미지 처리 (2026-09-05) — 「환경설정 > 기본환경」.
               ★ 거르개에 옵션 하나를 넘길 뿐이다. 그린 뒤 감추지 않는다 — CSS 로 감추면
                 그림은 이미 **받아 온 뒤**라, 보낸 쪽이 「열었다」를 알게 된다. */
            dangerouslySetInnerHTML={{
              // 2026-10-14 — 바깥 그림은 서버 프록시로 돌린다(보낸 쪽이 끼워 넣기를 막은 그림 · mail-image-proxy.ts).
              __html: proxyExternalImages(isExternal
                ? sanitizeExternalMailHtml(detail.body, { blockImages })
                // 2026-10-14 — 우리가 쓴 메일은 AxiEditor 서식(style 허용목록)을 살린다. 종전 `sanitizeMailHtml`
                //   의 상위집합이고, class·id 는 에디터 이름공간 밖이면 지운다.
                : sanitizeMailHtmlRich(detail.body, { blockImages }), mailId),
            }}
          />
        ) : (
          <pre className="mail-detail-body-text">{detail.body}</pre>
        )}
      </div>

      {/* 첨부 — backend 4-tier OR 가드 (mail recipient/sender 분기) 가 권한 검증.
          AttachmentList 의 ul 이 이미 aria-label="첨부 파일" 보유 → wrapper 의 추가 label 은 중첩
          announce 유발하므로 생략 (Loop1 review MEDIUM 봉합). */}
      {/* 머리줄이 먼저 답한다 — 몇 개, 얼마. 목록은 여태 쓰던 AttachmentList 그대로다. */}
      {/* mailId 를 함께 넘긴다 — 「워크스페이스에 저장」 권한을 메일 기준으로 보기 때문 (2026-09-02). */}
      {/* 2026-10-14 — 본문 그림(`inline: true`)은 본문에 이미 보인다 — 첨부 목록에서는 뺀다. */}
      <MailAttachments
        attachments={detail.attachments.filter((a) => (a as { inline?: boolean }).inline !== true)}
        {...(mailId ? { mailId } : {})}
      />

      {/*
        치우기 전에 묻는다 (2026-08-22) — 창 껍데기는 메일함 비우기에서 쓰던 것을 그대로 쓴다.
        ★ `danger` 를 켜지 않는다. 이 창의 규약은 "되돌릴 수 없으면 붉게" 인데, 휴지통도
          스팸도 되돌릴 수 있다. 붉게 칠하면 규약이 뜻을 잃는다.
      */}
      <MailConfirmDialog
        open={confirmMove !== null}
        busy={busy !== null}
        /* 초안 삭제만 되돌릴 수 없다 — 이 창의 규약이 "되돌릴 수 없으면 붉게" 다. */
        danger={confirmMove === 'trash' && detail.senderStatus?.isDraft === true}
        title={confirmMove === 'spam'
          ? tMail('mail.detail.confirm_spam_title')
          : tMail(trashConfirmKeys.title)}
        description={(confirmMove === 'spam'
          ? tMail('mail.detail.confirm_spam_desc')
          : tMail(trashConfirmKeys.desc)
        ).replace('{subject}', detail.subject || tMail('mail.item.subject_empty'))}
        confirmLabel={confirmMove === 'spam'
          ? tMail('mail.detail.confirm_spam_ok')
          : tMail(trashConfirmKeys.ok)}
        onConfirm={() => { if (confirmMove === 'spam') void runSpam(true); else void runTrash(true); }}
        onCancel={() => setConfirmMove(null)}
      />

      {/*
        주소록에 담기 (2026-09-08 · 정본 E1-4).
        ★ 「전부」와 「이 사람 하나」가 **같은 표**를 쓴다 — 한 줄짜리라고 다른 화면을 만들면
          규칙(잠금 · 이유 적기 · 비운 이름 처리)이 둘로 갈린다.
      */}
      {pickList && pickList.length > 0 && (
        <ContactPickModal
          candidates={pickList}
          canShare={canShareContacts}
          onClose={() => setPickList(null)}
          onDone={() => setPickList(null)}
        />
      )}
    </section>
  );
}

/**
 * 이 메일에 있는 주소를 모두 모은다 — 보낸사람 · 받는사람 · 참조 · 숨은참조.
 *
 * ★ **나도 담는다(잠긴 줄로).** 목록에서 빼면 「왜 다섯인데 넷만 나오지」가 된다 —
 *   자리는 남기고 이유를 적는 것이 E1-4 의 규칙이다.
 * ★ 같은 주소가 여러 종류로 오면 **한 줄**이다(받는사람이자 참조인 사람).
 */
export function collectCandidates(
  detail: { sender: { id: string; name: string; email: string }; recipients: MailRecipientDetail[] },
  myUserId: string | null,
  myEmail?: string | null,
): PickCandidate[] {
  return toCandidates(
    [{ id: detail.sender.id, name: detail.sender.name, email: detail.sender.email }, ...detail.recipients.map((r) => ({ id: r.userId, name: r.name, email: r.email }))],
    myUserId, myEmail,
  );
}

/**
 * 주소 목록을 담기 후보로 — **나는 뺀다** (2026-09-08 사용자 지적).
 *
 * ★★★ 처음엔 나를 «잠긴 줄»로 남기고 「나 — 담지 않음」이라 적었는데, 사용자는
 *    **아예 빼기를** 원했다. 「이미 담긴 사람」과는 다른 물건이다 — 그쪽은 «담을 대상인데
 *    이미 있는» 사람이라 남길 값이 있지만, 나는 **애초에 담을 대상이 아니다.**
 * ⚠⚠⚠ 판정은 `userId` 로 한다. 처음에 `r.id` 로 비교했는데 그것은 **수신자 «행» id** 라
 *    사용자 id 와 절대 같지 않았다 — 그래서 **본인이 한 번도 안 걸러졌다**(실브라우저에서
 *    본인이 체크된 채로 떴다). 주소로도 한 번 더 거른다(사내/외부 어느 쪽으로 와도 걸리게).
 */
export function toCandidates(
  rows: Array<{ id?: string | null; name?: string | null; email: string }>,
  myUserId: string | null,
  myEmail?: string | null,
): PickCandidate[] {
  const mine = (myEmail || '').trim().toLowerCase();
  const out = new Map<string, PickCandidate>();
  for (const r of rows) {
    const key = (r.email || '').trim().toLowerCase();
    if (key === '') continue;
    if (myUserId != null && r.id != null && r.id !== '' && String(r.id) === myUserId) continue;
    if (mine !== '' && key === mine) continue;
    const prev = out.get(key);
    /* 이름은 «있는 쪽»이 이긴다 — 한 곳에서만 이름이 왔을 수 있다. */
    if (prev) { if (!prev.name && r.name) prev.name = r.name.trim() || null; continue; }
    out.set(key, { email: key, name: r.name?.trim() || null });
  }
  return [...out.values()];
}

/** HTML → plain text — Reply/Forward 의 quoted body 인용용. DOMPurify 후 텍스트만 추출. */
function stripHtml(body: string, bodyType: string): string {
  if (bodyType !== 'HTML') return body;
  // sanitize 한 후 태그 모두 제거 — XSS payload 가 quoted body 로 살아남는 것 차단.
  const safe = sanitizeMailHtml(body);
  return safe.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+\n/g, '\n').trim();
}

function formatDetailTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateTime(d); // Cycle 55 — bare default → lang-aware
}
